<template>
  <div class="value-input">

    <div>
      <div class="first">
        <label v-if="!!label">{{ label + ( isMandatory ? ' *' : '') }}</label>

        <select-list
          class="ut-mr-0 ut-hidden md:ut-block"
          :options="currentTypes"
          :disabled="disableSelect"
          v-model="select"
          @input="onSelect"
        />
      </div>

      <div class="second" v-show="needsInput">
        <div class="half first">
          <label>{{ value1Label }}</label>
          <input
            type="text"
            :placeholder="placeholder"
            v-model="value1"
            v-on:keypress="isNumber($event)"
            v-on:change="onInput"
            :class="[ isError ? 'error' : '']"
          />
        </div>
        <div v-if="isRange" class="half">
          <label>{{ value2Label }}</label>
          <input
            type="text"
            :placeholder="placeholder"
            v-model="value2"
            v-on:keypress="isNumber($event)"
            v-on:change="onInput"
            :class="[ isError ? 'error' : '']"
          />
        </div>
      </div>
    </div>

    <span v-if="isError" id="error-message" class="error inline">
      {{ errorMsg }}
    </span>

  </div>
</template>

<script>

import SelectList from '@/components/Forms/SelectList.vue';

const valueInputTypes = {
  exact_d: 'Exact $',
  fixed_d: 'Fixed $',
  fixed_p: 'Fixed %',
  greater_d: 'Greater Than $',
  range_d: 'Range $',
  range_p: 'Range %',
  accelerator: 'Accelerator',
  incommission: 'Included in Commission',
  free: 'Free',
  notprovided: 'Not Provided',
  includegst: 'Including GST',
  excludegst: 'Excluding GST',
  yes: 'Yes',
  no: 'No',
  offers_above_d: 'Offers above',
  range_price_d: 'Price range',
  onrequest: 'Price on request',
};

export default {
  name: 'ValueInput',
  components: {
    SelectList,
  },
  props: {
    label: {
      required: true,
    },
    valueInputIds: {
      required: true,
    },
    isMandatory: {
      default: false,
    },
    disableSelect: {
      default: false,
    },
    defaultSelect: {
      default: '',
    },
    defaultValue1: {
      default: '',
    },
    defaultValue2: {
      default: '',
    },
  },
  computed: {
    placeholder() {
      if (this.select) {
        if (this.select.endsWith('_d')) {
          return '$ ...';
        }
        if (this.select.endsWith('_p')) {
          return '% ...';
        }
      }
      return '...';
    },
    needsInput() {
      return this.select
        && (this.select.endsWith('_d')
        || this.select.endsWith('_p'));
    },
    isPercent() {
      return this.select
        && this.select.endsWith('_p');
    },
    isRange() {
      return this.select
        && this.select.startsWith('range_');
    },
    value1Label() {
      if (this.needsInput) {
        if (this.isRange) {
          return 'Low';
        }
        return 'Value';
      }
      return '';
    },
    value2Label() {
      if (this.needsInput) {
        if (this.isRange) {
          return 'High';
        }
      }
      return '';
    },
    isError() {
      return this.errorMsg !== '';
    },
    valuesObject() {
      return {
        value1: this.value1,
        value2: this.value2,
        select: this.select,
        selectDisplay: valueInputTypes[this.select] || '',
        error: this.isError,
        label: this.label,
      };
    },
  },
  created() {
    this.generateSelect();
    this.$parent.$on('validate-fields', this.validate);
  },
  data() {
    return {
      currentTypes: [],
      errorMsg: '',
      select: this.defaultSelect,
      value1: this.defaultValue1,
      value2: this.defaultValue2,
    };
  },
  methods: {
    generateSelect() {
      this.currentTypes.push({
        value: '',
        text: 'Please select...',
        selected: this.defaultSelect === '',
      });
      for (let i = 0; i < this.valueInputIds.length; i += 1) {
        if (Object.prototype.hasOwnProperty.call(valueInputTypes, this.valueInputIds[i])) {
          this.currentTypes.push({
            value: this.valueInputIds[i],
            text: valueInputTypes[this.valueInputIds[i]],
            selected: this.valueInputIds[i] === this.defaultSelect,
          });
        }
      }
    },
    isNumber(evt) {
      const event = evt || window.event;
      const charCode = (event.which) ? event.which : event.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        event.preventDefault();
        return false;
      }
      return true;
    },
    validate() {
      this.errorMsg = '';

      if (this.isMandatory && this.select === '') {
        this.errorMsg = 'Please select a value from the dropdown.';
      } else if (this.isMissingInput()) {
        this.errorMsg = 'Please enter a value.';
      } else if (this.isInconsistantRange()) {
        this.errorMsg = 'Low value should not be higher than High value. ';
      } else if (this.isInconsistantPercentage()) {
        this.errorMsg = 'Please enter a valid percentage.';
      }

      this.$emit('validated-fields', this.valuesObject);
    },
    isMissingInput() {
      return this.needsInput
        && (this.value1 === ''
        || (this.isRange && this.value2 === ''));
    },
    isInconsistantRange() {
      return this.isRange
        && !this.isMissingInput()
        && parseFloat(this.value1) > parseFloat(this.value2);
    },
    isInconsistantPercentage() {
      return this.isPercent
        && !this.isMissingInput()
        && (parseFloat(this.value1) > 100
          || (this.isRange && parseFloat(this.value2) > 100)
        );
    },
    onInput() {
      this.$emit('input', this.valuesObject);
    },
    onSelect(value) {
      this.select = value;
      this.value1 = '';
      this.value2 = '';
      this.errorMsg = '';
      this.onInput();
    },
  },
};
</script>

<style lang="scss" scoped>

label {
  @apply ut-font-bold ut-text-sm ut-pb-3 ut-tracking-normal;
  line-height: 24px;
}

.first {
  display: inline-block;
  @apply ut-w-full;
  @screen md {
    width: 58%;
  }
  margin-right: 10px;
}

.second {
  display: inline-block;
  @apply ut-w-full;
  @screen md {
    width: 40%;
  }
}

.half {
  display: inline-block;
  width: 50%;
}

.half.first {
  width: 48%;
  margin-right: 5px;
}

</style>
